import React from 'react';
import { DeadMansSwitch } from '@/components/dashboard/DeadMansSwitch';
import HeartbeatMonitor from '@/components/dashboard/HeartbeatMonitor';
import { TenantMap } from '@/components/dashboard/TenantMap';
import { Move37Feed } from '@/components/dashboard/Move37Feed';
import { NotebookLMTab } from '@/components/dashboard/NotebookLMTab';

/**
 * The preserved blueprint surfaces.
 *
 * Every block here was already flagged as a blueprint in the Command tab bar —
 * the operations overview cards, the NotebookLM bridge, the dead man's switch,
 * the heartbeat monitor, the tenant map and the Move 37 feed. They moved here
 * by composition: the components are imported and mounted exactly as they were,
 * nothing was deleted, and each block carries the literal "no source yet" label
 * so no operator can mistake preserved scaffolding for live telemetry.
 */
function BlueprintBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    // The preserved components keep their own layout — some of it is wider than a
    // phone. The block scrolls that content sideways instead of letting it push
    // the page into a horizontal scroll.
    <div className="overflow-x-auto rounded-lg border border-gray-800 bg-gray-900/20 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-mono uppercase tracking-wider text-gray-400">{title}</h3>
        <span className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70">no source yet</span>
      </div>
      {children}
    </div>
  );
}

export function CommandBlueprintsPage() {
  return (
    <section aria-label="Blueprints">
      <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-800 pb-2">
        <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-white">Blueprints</h2>
        <span className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70">Preserved</span>
      </div>

      <p className="mb-4 text-xs font-mono text-gray-400">
        Command surfaces whose source is not wired yet. Every block below is labelled
        <span className="text-yellow-500/80"> no source yet</span>: none of it is live telemetry.
      </p>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr_350px]">
        {/* Column 1: System control */}
        <div className="flex flex-col gap-4">
          <BlueprintBlock title="Dead Man's Switch">
            <DeadMansSwitch />
          </BlueprintBlock>
          <BlueprintBlock title="Heartbeat">
            <HeartbeatMonitor />
          </BlueprintBlock>

          {/* Preserved blueprint until the real operations event stream is recovered. */}
          <BlueprintBlock title="System_Log">
            <div className="text-[10px] text-gray-600 font-mono space-y-1">
              <p>&gt; Pre-split operations log placeholder preserved.</p>
              <p>&gt; Awaiting real event source recovery.</p>
              <p>&gt; Moltbook / Move37 / tenant map archaeology required.</p>
              <p className="text-yellow-500/80">&gt; DO NOT TREAT AS LIVE TELEMETRY</p>
            </div>
          </BlueprintBlock>
        </div>

        {/* Column 2: Tenant map */}
        <div>
          <BlueprintBlock title="Tenant Cluster Map">
            <TenantMap />
          </BlueprintBlock>
        </div>

        {/* Column 3: Operations */}
        <div>
          <BlueprintBlock title="Move 37 Feed">
            <Move37Feed />
          </BlueprintBlock>
        </div>
      </div>

      <div className="mt-4">
        <BlueprintBlock title="NotebookLM Bridge">
          <NotebookLMTab />
        </BlueprintBlock>
      </div>
    </section>
  );
}
